import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';

/**
 * Tour — guia "how to" in-app, genérico e sem dependências externas.
 *
 * Replicável em outros projetos: copie este arquivo e alimente com `steps`.
 * Cada step aponta para um elemento da tela via seletor CSS (use `data-tour="..."`
 * nos elementos-alvo). Sem `target`, o step é exibido centralizado (intro/encerramento).
 *
 *   <Tour steps={[{ target: '[data-tour="kpis"]', title: '...', body: '...' }]} onClose={...} />
 *
 * O componente cuida de: spotlight no alvo, balão posicionado (com clamp na viewport),
 * scroll automático até o alvo, recálculo em scroll/resize, e navegação por teclado
 * (→/Enter próximo, ← anterior, Esc fecha).
 */

export interface TourStep {
  /** Seletor CSS do alvo (ex.: '[data-tour="inst-kpis"]'). Ausente → step centralizado. */
  target?: string;
  title: string;
  /** Texto corrido (opcional). Use como lead curto quando combinado com `bullets`. */
  body?: string;
  /** Tópicos — para passos com várias ações/informações, mais claros que texto corrido.
   *  Cada item pode ser texto simples ou `{ text, color }` (cor do marcador, ex.: legenda de cores). */
  bullets?: (string | { text: string; color?: string })[];
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  /** Ação ao entrar no passo (ex.: abrir um painel para destacar). Deve ser idempotente. */
  onEnter?: () => void;
}

export interface TourLabels {
  skip: string;
  prev: string;
  next: string;
  done: string;
}

const C = { card: '#0d1e2e', border: '#1e3a5f', muted: '#8fa8c8', gold: '#c9a84c', white: '#eef4fc' };
const DEFAULT_LABELS: TourLabels = { skip: 'Skip', prev: 'Back', next: 'Next', done: 'Done' };

interface Rect { top: number; left: number; width: number; height: number }

export default function Tour({ steps, onClose, labels = DEFAULT_LABELS }: {
  steps: TourStep[];
  onClose: () => void;
  labels?: TourLabels;
}) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [pop, setPop] = useState<{ top: number; left: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const step = steps[i];

  const measure = useCallback(() => {
    if (!step?.target) { setRect(null); return; }
    const el = document.querySelector(step.target) as HTMLElement | null;
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  // Ao mudar de step: executa a ação do passo (se houver), leva o alvo à viewport e mede.
  // Remede algumas vezes para acomodar alvos que só renderizam após a ação (onEnter).
  useEffect(() => {
    try { step?.onEnter?.(); } catch { /* ignora falhas de ação do tour */ }
    const sync = () => {
      if (step?.target) {
        const el = document.querySelector(step.target) as HTMLElement | null;
        el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
      measure();
    };
    sync();
    const timers = [setTimeout(sync, 90), setTimeout(sync, 340), setTimeout(sync, 650)];
    return () => timers.forEach(clearTimeout);
  }, [i, measure, step]);

  // Mantém o spotlight alinhado em scroll/resize.
  useEffect(() => {
    const fn = () => measure();
    window.addEventListener('scroll', fn, true);
    window.addEventListener('resize', fn);
    return () => { window.removeEventListener('scroll', fn, true); window.removeEventListener('resize', fn); };
  }, [measure]);

  // Teclado.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') setI((v) => Math.min(v + 1, steps.length - 1));
      else if (e.key === 'ArrowLeft') setI((v) => Math.max(v - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, steps.length]);

  // Posiciona o balão relativo ao alvo (ou centraliza), com clamp na viewport.
  useLayoutEffect(() => {
    const popEl = popRef.current;
    if (!popEl) return;
    const pw = popEl.offsetWidth, ph = popEl.offsetHeight;
    const vw = window.innerWidth, vh = window.innerHeight;
    const margin = 12, gap = 14;
    if (!rect) { setPop({ top: Math.round(vh / 2 - ph / 2), left: Math.round(vw / 2 - pw / 2) }); return; }
    let placement = step.placement ?? 'auto';
    if (placement === 'auto') {
      placement = (rect.top + rect.height + gap + ph < vh) ? 'bottom'
        : (rect.top - gap - ph > 0) ? 'top'
        : (rect.left + rect.width + gap + pw < vw) ? 'right' : 'left';
    }
    let top = 0, left = 0;
    if (placement === 'bottom') { top = rect.top + rect.height + gap; left = rect.left + rect.width / 2 - pw / 2; }
    else if (placement === 'top') { top = rect.top - gap - ph; left = rect.left + rect.width / 2 - pw / 2; }
    else if (placement === 'right') { left = rect.left + rect.width + gap; top = rect.top + rect.height / 2 - ph / 2; }
    else { left = rect.left - gap - pw; top = rect.top + rect.height / 2 - ph / 2; }
    top = Math.max(margin, Math.min(top, vh - ph - margin));
    left = Math.max(margin, Math.min(left, vw - pw - margin));
    setPop({ top: Math.round(top), left: Math.round(left) });
  }, [rect, i, step]);

  if (!step) return null;
  const isFirst = i === 0, isLast = i === steps.length - 1, pad = 6;
  const ghost: React.CSSProperties = { background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 7, color: C.muted, cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '6px 12px', fontFamily: 'inherit' };
  const primary: React.CSSProperties = { background: C.gold, border: 'none', borderRadius: 7, color: '#081722', cursor: 'pointer', fontSize: 12, fontWeight: 700, padding: '6px 14px', fontFamily: 'inherit' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
      {/* Camada que bloqueia interação (escurece a tela quando não há alvo). */}
      <div style={{ position: 'absolute', inset: 0, background: rect ? 'transparent' : 'rgba(0,0,0,0.65)' }} />
      {/* Spotlight no alvo. */}
      {rect && (
        <div style={{
          position: 'absolute', top: rect.top - pad, left: rect.left - pad,
          width: rect.width + pad * 2, height: rect.height + pad * 2,
          borderRadius: 10, boxShadow: '0 0 0 9999px rgba(0,0,0,0.65)',
          border: `2px solid ${C.gold}`, pointerEvents: 'none', transition: 'all 0.2s ease',
        }} />
      )}
      {/* Balão. */}
      <div ref={popRef} style={{
        position: 'absolute', top: pop?.top ?? -9999, left: pop?.left ?? -9999,
        width: 320, maxWidth: 'calc(100vw - 24px)', background: C.card,
        border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
        padding: '16px 18px', zIndex: 2001,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.white, marginBottom: 6 }}>{step.title}</div>
        {step.body && (
          <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, marginBottom: step.bullets?.length ? 8 : 14 }}>{step.body}</div>
        )}
        {step.bullets?.length ? (
          <ul style={{ margin: '0 0 14px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {step.bullets.map((b, k) => {
              const text = typeof b === 'string' ? b : b.text;
              const marker = typeof b === 'string' ? C.gold : (b.color ?? C.gold);
              return (
                <li key={k} style={{ display: 'flex', gap: 8, fontSize: 13, color: C.muted, lineHeight: 1.45 }}>
                  <span style={{ color: marker, flexShrink: 0, marginTop: 1 }}>•</span>
                  <span>{text}</span>
                </li>
              );
            })}
          </ul>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <button onClick={onClose} style={ghost}>{labels.skip}</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, color: C.muted }}>{i + 1}/{steps.length}</span>
            {!isFirst && <button onClick={() => setI((v) => v - 1)} style={ghost}>{labels.prev}</button>}
            <button onClick={() => (isLast ? onClose() : setI((v) => v + 1))} style={primary}>{isLast ? labels.done : labels.next}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
